/**
 * Agent registry — single source of truth for agent metadata used by both the
 * hero on the home page and the per-agent placeholder pages.
 *
 * Adding a new agent is a single entry here: the AgentRail, the AgentMeta
 * stack, and the home "What <agent> can do" section all derive their content
 * from this map.
 */

const AGENT_SLUGS = ['uno', 'vera', 'maximus'] as const;
export type AgentSlug = (typeof AGENT_SLUGS)[number];

export type AgentStatus = 'ONLINE' | 'TRAINING' | 'OFFLINE';

/** A single marketing highlight of an agent's capability, shown in the home
 *  "What <agent> can do" section for available agents. */
export interface AgentCapability {
  /** Short mono tag, e.g. "WEB". */
  tag: string;
  /** One-sentence description of what the agent can do. */
  text: string;
}

export interface Agent {
  slug: AgentSlug;
  /** Display name. */
  name: string;
  /** Long-form role label, e.g. "PERCEPTION CORE". */
  role: string;
  /** Short label shown under the agent button. */
  shortRole: string;
  /** Sequence label, e.g. "AGENT_001". */
  agentNumber: string;
  /** Two-sentence pitch shown in the hero meta block. */
  blurb: string;
  /** Operational status; drives the chip styling on the home and agent pages. */
  status: AgentStatus;
  /**
   * Whether the in-app console (`/agent/:slug`) is reachable yet. Unavailable
   * agents still appear on the home page and chooser but can't be entered —
   * the chooser disables them and the intro/console routes redirect away.
   */
  available: boolean;
  /**
   * Cognito user-pool groups allowed to enter this agent (ANY-of). Omitted
   * ⇒ every signed-in user. Checked together with `available` by
   * `isAgentEnabledFor` (agentAccess.ts) at every entry decision point.
   * UX only — the agents runtime enforces the same gate authoritatively
   * (its `vera.json` carries matching `requiredGroups`).
   */
  requiredGroups?: readonly string[];
  /** Show a "Beta" badge on the chooser card (agent is live but still
   *  maturing). Purely presentational. */
  beta?: boolean;
  /** Public path to the portrait image. */
  image: string;
  /** Marketing highlights of what this agent can do. Rendered in the home
   *  "What <agent> can do" section (hover) for available agents. */
  capabilities?: readonly AgentCapability[];
}

export const AGENTS: Record<AgentSlug, Agent> = {
  uno: {
    slug: 'uno',
    name: 'Uno',
    role: 'PERCEPTION CORE',
    shortRole: 'Perception',
    agentNumber: 'AGENT_001',
    blurb:
      'Searches the web, tracks weather and markets, and keeps your documents and context close. Every other agent starts where Uno leaves off.',
    status: 'ONLINE',
    available: true,
    image: '/images/agents/uno.png',
    capabilities: [
      {
        tag: 'CORE',
        text: 'The perception core the entire OculusMind family is built on — every other agent starts here.',
      },
      {
        tag: 'WEB',
        text: 'Searches the live web for current information and brings back cited sources.',
      },
      {
        tag: 'WEATHER',
        text: 'Pulls live conditions and forecasts for any location you ask about.',
      },
      {
        tag: 'MARKETS',
        text: 'Looks up real-time stock prices and market information.',
      },
      {
        tag: 'DOCS',
        text: 'Creates, stores, and updates documents — notes, lists, and reports that persist across sessions and are yours to keep.',
      },
      {
        tag: 'MEMORY',
        text: 'Remembers your preferences and context, carrying them across conversations.',
      },
      {
        tag: 'EMAIL',
        text: 'Email your agent and get replies by email — it acts on your behalf, fully in context. Included with your free account.',
      },
      {
        // Outbound only: texts TO the shared stage number route to Vera, so
        // Uno's card promises delivery, not two-way chat.
        tag: 'SMS',
        text: 'Delivers answers and scheduled results by text, straight to your phone. Create your free account and verify your phone to enable it.',
      },
    ],
  },
  vera: {
    slug: 'vera',
    name: 'Vera',
    role: 'REASONING ENGINE',
    shortRole: 'Reasoning',
    agentNumber: 'AGENT_002',
    blurb:
      'Weighs evidence and verifies facts against multiple sources. Turns your repeatable work into custom workflows — and runs them for you on a schedule.',
    // GA launch 2026-07-07: available to every signed-in user in beta AND
    // prod. Mirrors the runtime's vera.json (stages: ["beta","prod"], no
    // requiredGroups) — the server gate is authoritative, this is the UX
    // mirror. No Beta badge: Vera is GA, not a preview.
    status: 'ONLINE',
    available: true,
    image: '/images/agents/vera.png',
    capabilities: [
      {
        tag: 'ALL+',
        text: 'Everything Uno can do — plus weighing evidence, verifying high-stakes facts, and automating your workflows.',
      },
      {
        tag: 'VERIFY',
        text: 'Weighs evidence and checks high-stakes facts against independent sources before asserting them.',
      },
      {
        tag: 'WORKFLOWS',
        text: 'Builds custom multi-step workflows from your instructions — yours to run, edit, and reuse.',
      },
      {
        tag: 'SCHEDULE',
        text: 'Schedules workflows to run automatically and delivers the results by email or text.',
      },
      {
        tag: 'IMAGES',
        text: 'Generates images from your prompts — delivered in chat and saved to your studio to view, download, or keep.',
      },
      {
        tag: 'WEB',
        text: 'Searches the live web for current information and brings back cited sources.',
      },
      {
        tag: 'MARKETS',
        text: 'Looks up real-time prices, fundamentals, earnings, and insider activity.',
      },
      {
        tag: 'DOCS',
        text: 'Creates, stores, and updates documents that persist across sessions and are yours to keep.',
      },
      {
        tag: 'MEMORY',
        text: 'Remembers your preferences and context, carrying them across conversations.',
      },
      {
        tag: 'EMAIL',
        text: 'Email your agent and get replies by email — it acts on your behalf, fully in context. Included with your free account.',
      },
      {
        // Two-way: the shared stage toll-free number routes inbound texts to
        // Vera (infra `sms.defaultAgentId`, user decision 2026-07-29).
        tag: 'SMS',
        text: 'Text your agent and it replies by SMS, in context with the rest of your conversation. Create your free account and verify your phone to enable it.',
      },
    ],
  },
  maximus: {
    slug: 'maximus',
    name: 'Maximus',
    role: 'ACTION OPERATOR',
    shortRole: 'Action',
    agentNumber: 'AGENT_003',
    blurb:
      'Interacts with external systems on your behalf — website forms, MCP servers, and your own tools. Going beyond search and analysis, Maximus acts.',
    // Maximus is an admin-only preview, now reachable in BOTH stages
    // (2026-08-01) — `available: true`, with the narrow group list doing all
    // the gating. Every other tier (guest/Free/Premium/Plus/Pro/VIP) sees the
    // disabled "in training" card rather than nothing, in beta and prod
    // alike. Mirrors the runtime's maximus.json
    // (`stages:['beta','prod']`, `requiredGroups:['admin','test']`) —
    // the server gate is authoritative, this is the UX mirror. 'test' (the
    // e2e/eval harness's ephemeral users) is listed here too so the mirror
    // matches the runtime exactly and a harness user is not shown a disabled
    // card it can in fact enter. Status stays TRAINING (a preview) while its
    // action surface is built.
    status: 'TRAINING',
    available: true,
    beta: true,
    requiredGroups: ['admin', 'test'],
    image: '/images/agents/maximus.png',
    capabilities: [
      {
        tag: 'ALL+',
        text: 'Everything Uno and Vera can do — plus interacting with external systems: website forms, MCP servers, and your own machines.',
      },
      {
        tag: 'ACT',
        text: 'Interacts with websites on your behalf — filling forms, posting data, completing multi-step tasks.',
      },
      {
        tag: 'TOOLS',
        text: 'Bring your own tools — connect your systems and let Maximus operate them.',
      },
      {
        tag: 'MCP',
        text: 'Finds, configures, and tests MCP server tools from any provider — extending its toolkit on demand.',
      },
      {
        tag: 'IMAGES',
        text: 'Generates images from your prompts — delivered in chat and saved to your studio to view, download, or keep.',
      },
      {
        tag: 'LOCAL',
        text: 'Runs commands on your own computers through a separately installed connector.',
      },
    ],
  },
};

export const AGENT_LIST: readonly Agent[] = AGENT_SLUGS.map((slug) => AGENTS[slug]);


export function isAgentSlug(value: string): value is AgentSlug {
  return (AGENT_SLUGS as readonly string[]).includes(value);
}

/**
 * Whether an agent is publicly live (GA) — `available` AND not group-gated.
 * Drives the home hero's PUBLIC "can do" vs "will do / IN TRAINING" tense:
 * an admin-gated preview (Maximus) reads "will do" to the general public on
 * every stage, even in beta builds where it is technically `available` for the
 * admin chooser. Distinct from per-user `isAgentEnabledFor` (who can enter).
 */
export function isPubliclyLive(agent: Agent): boolean {
  return agent.available && (agent.requiredGroups?.length ?? 0) === 0;
}

